import {CommonModule} from '@angular/common';
import {Component, computed, Inject, OnInit} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  ValidationErrors
} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectChange} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {
  BaseDialogComponent,
  isEmail,
  LicenseService,
  PLAN_TYPES,
  SessionService,
  USER_ROLE,
  UserTeam
} from '@q9elements/ui-kit/common';
import {
  DialogCloseButtonComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  SelectModule,
  SuggestionChipGridComponent,
  TextFieldComponent
} from '@q9elements/ui-kit/components';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {get} from 'lodash';
import {Observable, of} from 'rxjs';

import {InviteUserOrBatchApiPayload} from '../../models/spaces.interface';
import {PERMISSIONS} from '../../models/user-permissions.enum';

interface InviteUserDialogData {
  space: UserTeam;
  onSubmit: (
    id: string,
    data: InviteUserOrBatchApiPayload,
    intercomData: {
      email: string;
      space: string | undefined;
      isEditor: boolean;
    }
  ) => Observable<unknown>;
}

@Component({
  selector: 'q9ma-invite-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTooltipModule,
    TranslateModule,
    DialogCloseButtonComponent,
    InfoMessageComponent,
    TextFieldComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    SuggestionChipGridComponent,
    SelectModule
  ],
  templateUrl: './invite-user-dialog.component.html',
  styleUrls: ['./invite-user-dialog.component.scss']
})
export class InviteUserDialogComponent extends BaseDialogComponent implements OnInit {
  inviteUserForm: FormGroup;
  spaceName: string;

  protected readonly PLAN_TYPES = PLAN_TYPES;
  private readonly PERMISSIONS = PERMISSIONS;

  readonly isAdmin = this.data.space.teamAdmin;
  readonly isEditor = this.data.space.role === 'author';
  readonly isProSpace = this.data.space.team.plan.type === PLAN_TYPES.PRO;
  readonly isProPlus = this.data.space.team.plan.proPlus;
  readonly isNonBillable = this.data.space.team.plan.isNonBillable || false;
  readonly isProTrialExpired = this.data.space.team.plan.trialExpired || false;
  readonly isProActive =
    this.isProSpace &&
    (!this.data.space.team.plan.trial ||
      (this.data.space.team.plan.trial && !this.isProTrialExpired));
  readonly licenseFlags = get(this.data.space, 'team.licenseFlags', {});
  readonly proPlus = computed(() => get(this.data.space, 'team.plan.proPlus', false));
  public readonly licensesDetails = this.licenseService.licensesDetails;
  public readonly USER_ROLES = USER_ROLE;

  editorEnabled = this.isProActive ? this.isAdmin : this.isEditor;
  currentUser: UserTeam;

  private readonly permissionsArray = [
    'teamAdmin',
    'teamAdmin',
    'resourceAdmin',
    'dataStructureAdmin',
    'urlLibraryManager',
    'releaseAdmin',
    'requirementsManager',
    'importManager',
    'workflowIntegrationManager'
  ];

  private readonly editorRightsPermissions = [
    'editorOnAllMaps',
    'editorOnAllRefModels',
    'editorOnAllSFOrgs'
  ];

  private readonly mapPermissions = ['canCopyMap', 'canGrantAccessToMap', 'canAddNotesToMap'];

  constructor(
    public dialogRef: MatDialogRef<InviteUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: InviteUserDialogData,
    private sessionService: SessionService,
    private licenseService: LicenseService
  ) {
    super();
  }

  ngOnInit() {
    this.spaceName = `“${this.data.space?.team.name}” ` as string;
    this.currentUser = this.sessionService.user()?.userInTeam as UserTeam;

    this.initForm();
    this.subscribeToEditorOnAllMapsControl();
  }

  private initForm(): void {
    this.inviteUserForm = new FormGroup({
      emails: new FormControl([], [this.emailsRequired]),
      role: new FormControl(USER_ROLE.VIEWER),
      isEditor: new FormControl(false),
      teamAdmin: new FormControl({value: false, disabled: true}),
      resourceAdmin: new FormControl({value: false, disabled: true}),
      releaseAdmin: new FormControl({value: false, disabled: true}),
      dataStructureAdmin: new FormControl({value: false, disabled: true}),
      requirementsManager: new FormControl({value: false, disabled: true}),
      urlLibraryManager: new FormControl({value: false, disabled: true}),
      importManager: new FormControl({value: false, disabled: true}),
      message: new FormControl('', Q9Validators.maxLength(5000)),
      grantedEditRights: this.createGrantedEditRightsGroup(),
      workflowIntegrationManager: new FormControl({value: false, disabled: true})
    });
  }

  private createGrantedEditRightsGroup(): FormGroup {
    const grantedEditRightsGroup = new FormGroup({});

    for (const permission of this.editorRightsPermissions) {
      grantedEditRightsGroup.addControl(
        permission,
        new FormControl({value: false, disabled: true})
      );
    }

    for (const permission of this.mapPermissions) {
      grantedEditRightsGroup.addControl(
        permission,
        new FormControl({value: permission === 'canAddNotesToMap', disabled: true})
      );
    }

    return grantedEditRightsGroup;
  }

  private isControlDisabled(permission: string): boolean {
    const isFreeSpacePermission = ['teamAdmin', 'resourceAdmin'].includes(permission);

    switch (permission) {
      case this.PERMISSIONS.UrlLibraryManager:
        return !this.licenseFlags.urlLibrary;

      case this.PERMISSIONS.RequirementsManager:
        return !this.licenseFlags.changeTickets;

      case this.PERMISSIONS.WorkflowIntegrationManager:
        return (
          !this.licenseFlags.webhooksAndAPIs &&
          !this.licenseFlags.devOpsIntegration &&
          !this.licenseFlags.changeTicketsIntegration
        );

      case this.PERMISSIONS.DataStructureAdmin:
        return !this.licenseFlags.dataTables;
    }

    if (!isFreeSpacePermission && !this.isProActive) {
      return true;
    }

    if (!this.editorEnabled) {
      return true;
    }

    if (this.isProActive && !this.currentUser.teamAdmin) {
      return true;
    }

    if (!this.currentUser.teamAdmin) {
      return !this.currentUser[permission];
    }

    return false;
  }

  private emailsRequired(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!Array.isArray(value) || !value.length) {
      return {emailsRequired: true};
    }

    return null;
  }

  readonly displayEmail = (email: string): string => email;

  readonly validateEmail = (input: string): boolean => isEmail(input);

  readonly convertEmail = (raw: string): string => raw.trim().toLowerCase();

  get emailsErrorKey(): string {
    return this.inviteUserForm?.controls['emails']?.hasError('emailsRequired')
      ? 'TEAMS.INVITE_USER_EMAIL_REQUIRED'
      : 'TEAMS.INVITE_USER_EMAIL_ERROR';
  }

  protected onRoleChange(event: MatSelectChange): void {
    const spaceRole = event.value as USER_ROLE;
    const isEditorOrAdmin = spaceRole !== USER_ROLE.VIEWER;

    this.editorEnabled = isEditorOrAdmin;
    this.inviteUserForm.controls['isEditor'].setValue(isEditorOrAdmin);

    if (!isEditorOrAdmin) {
      for (const permission of this.permissionsArray) {
        this.inviteUserForm.controls[permission]?.setValue(false);
        this.inviteUserForm.controls[permission]?.disable();
      }

      for (const permission of this.editorRightsPermissions) {
        this.grantedEditRights.controls[permission].setValue(false);
        this.grantedEditRights.controls[permission].disable();
      }
    } else {
      for (const permission of this.permissionsArray) {
        if (['teamAdmin', 'resourceAdmin'].indexOf(permission) === -1 && !this.isProActive) {
          continue;
        }

        if (permission === 'teamAdmin') {
          if (spaceRole === USER_ROLE.MANAGER && !this.isControlDisabled(permission)) {
            this.inviteUserForm.controls[permission].enable();
            this.inviteUserForm.controls[permission].setValue(true);
          } else {
            this.inviteUserForm.controls[permission].setValue(false);
            this.inviteUserForm.controls[permission].disable();
          }

          continue;
        }

        if (this.isControlDisabled(permission)) {
          this.inviteUserForm.controls[permission].disable();
        } else {
          this.inviteUserForm.controls[permission].enable();
          this.inviteUserForm.controls[permission].setValue(true);
        }
      }

      for (const permission of this.editorRightsPermissions) {
        if (!this.isProActive) {
          continue;
        }

        if (this.isControlDisabled(permission)) {
          this.grantedEditRights.controls[permission].disable();
        } else {
          this.grantedEditRights.controls[permission].enable();
          this.grantedEditRights.controls[permission].setValue(true);
        }
      }
    }
  }

  get grantedEditRights(): FormGroup {
    return this.inviteUserForm.get('grantedEditRights') as FormGroup;
  }

  get editorOnAllMaps(): AbstractControl {
    return this.grantedEditRights.get('editorOnAllMaps')!;
  }

  private subscribeToEditorOnAllMapsControl(): void {
    this.editorOnAllMaps.valueChanges
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((enabled: boolean) => {
        for (const permission of this.mapPermissions) {
          if (enabled) {
            this.grantedEditRights.controls[permission].enable();
          } else {
            this.grantedEditRights.controls[permission].setValue(false);
            this.grantedEditRights.controls[permission].disable();
          }
        }
      });
  }

  override onSubmit() {
    this.inviteUserForm.markAllAsTouched();

    if (this.inviteUserForm.invalid) {
      return of(() => void 0).subscribe();
    }

    return super.onSubmit();
  }

  protected override sendSubmitRequest(): Observable<unknown> {
    const teamId = this.data?.space.team.id as string;
    const {emails = [], ...rest} = this.inviteUserForm.value;
    const normalizedEmails = (Array.isArray(emails) ? emails : []).map(e =>
      String(e).trim().toLowerCase()
    );
    const permissions: InviteUserOrBatchApiPayload =
      normalizedEmails.length > 1
        ? {...rest, emails: normalizedEmails}
        : {...rest, email: normalizedEmails[0] ?? ''};

    const intercomData = {
      email: emails[0] ?? '',
      space: this.currentUser?.organization?.name,
      isEditor: this.inviteUserForm.controls['isEditor'].value
    };

    return this.data.onSubmit?.(teamId, permissions, intercomData) as Observable<unknown>;
  }
}
